import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';

export type SurveyStatus = 'Draft' | 'Open' | 'Closed' | 'Archived';
export type SurveyQuestionType = 'Choice' | 'Scale' | 'Text';

export interface SurveyOptionView {
  id: string;
  text: string;
}

export interface SurveyQuestionView {
  id: string;
  type: string;
  prompt: string;
  required: boolean;
  options: SurveyOptionView[];
  scaleMin: number;
  scaleMax: number;
  scaleLabels?: string[] | null;
  allowMultiple: boolean;
}

export interface SurveyAudienceView {
  scope: string;
  ids: string[];
}

export interface SurveySummary {
  id: string;
  title: string;
  status: string;
  isAnonymous: boolean;
  deadline?: string | null;
  responseCount: number;
  completedRespondentCount: number;
  targetCount: number;
  questionCount: number;
}

export interface SurveyDetail {
  id: string;
  title: string;
  intro: string;
  status: string;
  isAnonymous: boolean;
  deadline?: string | null;
  questions: SurveyQuestionView[];
  audience: SurveyAudienceView;
  targetCount: number;
  myResponded: boolean;
}

export interface ChoiceResult {
  optionId: string;
  optionText: string;
  count: number;
}

export interface ScaleBucket {
  value: number;
  count: number;
}

export interface QuestionResult {
  questionId: string;
  prompt: string;
  type: string;
  choices: ChoiceResult[];
  scaleAverage: number;
  scaleDistribution: ScaleBucket[];
  textAnswers: string[];
}

export interface SurveyResults {
  surveyId: string;
  title: string;
  status: string;
  isAnonymous: boolean;
  responseCount: number;
  completedRespondentCount: number;
  targetCount: number;
  completionRate: number;
  questions: QuestionResult[];
}

export interface QuestionInput {
  type: string;
  prompt: string;
  required: boolean;
  options: string[];
  scaleMin: number;
  scaleMax: number;
  allowMultiple: boolean;
}

export interface CreateSurveyRequest {
  title: string;
  intro: string;
  questions: QuestionInput[];
  audience: SurveyAudienceView;
  isAnonymous: boolean;
  deadline?: string | null;
}

export interface AnswerInput {
  questionId: string;
  choiceOptionIds: string[];
  scaleValue?: number | null;
  textValue?: string | null;
}

/**
 * Phase V1.8 — surveys client. Board builds/publishes/reads results+export; residents in the audience
 * respond once. Anonymous surveys never expose an answer↔identity link (enforced server-side).
 */
@Injectable({ providedIn: 'root' })
export class UndersokelserService {
  private readonly http = inject(HttpClient);

  get(id: string): Observable<SurveyDetail> {
    return this.http.get<SurveyDetail>(`/api/surveys/${encodeURIComponent(id)}`);
  }
  create(req: CreateSurveyRequest): Observable<{ id: string }> {
    return this.http.post<{ id: string }>('/api/surveys', req);
  }
  publish(id: string): Observable<void> {
    return this.http.post<void>(`/api/surveys/${encodeURIComponent(id)}/publish`, {});
  }
  close(id: string): Observable<void> {
    return this.http.post<void>(`/api/surveys/${encodeURIComponent(id)}/close`, {});
  }
  respond(id: string, answers: AnswerInput[]): Observable<void> {
    return this.http.post<void>(`/api/surveys/${encodeURIComponent(id)}/responses`, { answers });
  }
  results(id: string): Observable<SurveyResults> {
    return this.http.get<SurveyResults>(`/api/surveys/${encodeURIComponent(id)}/results`);
  }
  exportHref(id: string, format: 'csv' | 'json'): string {
    return `/api/surveys/${encodeURIComponent(id)}/export?format=${format}`;
  }
}
